// ============================================================
// src/context/PendingRequestsContext.js
//
// Fetches and maintains a live count of:
//   - pending show requests       (show_requests)
//   - pending manager requests    (manager_client_relationships)
//   - pending gig mgmt requests   (management_requests)
//
// Provides { pendingShows, pendingManagers, pendingGigReqs, total }
// to any component via usePendingRequests().
//
// Used by Layout to show a badge on the Requests nav item and
// the bell icon in the top bar.
//
// Subscribes to realtime so the badge updates without refresh.
// ============================================================

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'

const PendingRequestsContext = createContext({
  pendingShows:    0,
  pendingManagers: 0,
  pendingGigReqs:  0,
  total:           0,
})

export function PendingRequestsProvider({ children }) {
  const [pendingShows,    setPendingShows]    = useState(0)
  const [pendingManagers, setPendingManagers] = useState(0)
  const [pendingGigReqs,  setPendingGigReqs]  = useState(0)
  // pendingGigReqs = count of pending rows in management_requests
  // where performer_id = the logged-in user

  const [userId, setUserId] = useState(null)

  // ── Get user ID once ──────────────────────────────────────
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id)
    })
  }, [])

  // ── Fetch counts ──────────────────────────────────────────
  const fetchCounts = useCallback(async () => {
    if (!userId) return

    // Count pending show requests
    const { count: showCount } = await supabase
      .from('show_requests')
      .select('id', { count: 'exact', head: true })
      .eq('performer_id', userId)
      .eq('status', 'pending')

    // Count pending manager relationship requests
    // performer_id here = profiles.id = auth user ID
    const { count: mgrCount } = await supabase
      .from('manager_client_relationships')
      .select('id', { count: 'exact', head: true })
      .eq('performer_id', userId)
      .eq('status', 'pending')

    // Count pending gig management requests
    // These are sent by managers via the Producer Pocket Planner to ask
    // permission to manage a specific gig on the performer's behalf
    const { count: gigReqCount } = await supabase
      .from('management_requests')
      .select('id', { count: 'exact', head: true })
      .eq('performer_id', userId)
      .eq('status', 'pending')

    setPendingShows(showCount    || 0)
    setPendingManagers(mgrCount  || 0)
    setPendingGigReqs(gigReqCount || 0)
  }, [userId])

  useEffect(() => { fetchCounts() }, [fetchCounts])

  // ── Realtime subscriptions ────────────────────────────────
  useEffect(() => {
    if (!userId) return

    // Watch show_requests for this performer
    const showSub = supabase
      .channel('pending-show-requests-count')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'show_requests',
        filter: `performer_id=eq.${userId}`,
      }, fetchCounts)
      .subscribe()

    // Watch manager_client_relationships for this performer
    const mgrSub = supabase
      .channel('pending-manager-requests-count')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'manager_client_relationships',
        filter: `performer_id=eq.${userId}`,
      }, fetchCounts)
      .subscribe()

    // Watch management_requests for this performer
    // Fires when a manager sends a new gig management request
    const gigReqSub = supabase
      .channel('pending-gig-requests-count')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'management_requests',
        filter: `performer_id=eq.${userId}`,
      }, fetchCounts)
      .subscribe()

    return () => {
      supabase.removeChannel(showSub)
      supabase.removeChannel(mgrSub)
      supabase.removeChannel(gigReqSub)
    }
  }, [userId, fetchCounts])

  const total = pendingShows + pendingManagers + pendingGigReqs
  // total = all three combined, used by the bell badge and nav badge

  return (
    <PendingRequestsContext.Provider value={{ pendingShows, pendingManagers, pendingGigReqs, total }}>
      {children}
    </PendingRequestsContext.Provider>
  )
}

// Hook for consuming the context
export function usePendingRequests() {
  return useContext(PendingRequestsContext)
}